import React from 'react';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match, RouteComponentProps } from 'react-router-dom';

// Components
import { LCAlert } from '../../../../components/alert';
import App from '../../../../components/app';
import ErrorMessage from '../../../../components/error-message';
import EnhancedForm from '../../../../components/form';
import { TooltipButton } from '../../../../components/tooltip-button';
import FormInput from '../../../../components/form/formInput';
import Listing from '../../../../components/listing';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import {
    actions as pricingActions,
    selectors as pricingSelectors,
    types as pricingTypes,
} from '../../../../redux/reducers/pricing';
import { selectors as pendingSelectors } from '../../../../redux/reducers/pendingOperations';
import { Area, AreaByID, TariffByID, TParams } from '../../../../redux/reducers/api-types';
// Ressources
import FormUtils from '../../../../utils/forms';
import withGlobalStyles from '../../../../utils/global-styles';
import ObjectUtils from '../../../../utils/objectUtils';
import UserUtils, { AccountRight, RightRank } from '../../../../utils/userUtils';
import { PricingPricesAddTariffHoroPage } from '../addTariffHoro/prices-add-tariff-horo';
import { paths } from '../../../paths';
import { ActionLogs } from '../../../../components/action-logs';
import { clsx } from '../../../../utils/htmlUtils';
import { FormDatas, FormPickerDatas } from '../../../../local-types';
import { generateSurgeOptions } from '../shared';

const queryString = require('query-string');

interface PricesAddTariffDispoProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    tariffs: TariffByID;
    areas: AreaByID;
    getPricesAddTariffError: ErrorPayload;
    isSavingTariff: boolean;
    getTariff: (id: string) => void;
    createTariff: (payload: any) => void;
    editTariff: (id: string, payload: any) => void;
    deleteTariff: (idp: string, idt: any) => void;
    getAreas: () => void;
}

interface PricingPricesAddTariffDispoState {
    formData: any;
    formPrice: any;
}

class PricingPricesAddTariffDispoPage extends React.Component<
    PricesAddTariffDispoProps,
    PricingPricesAddTariffDispoState
> {
    alertRef: any;

    isAdmin: boolean;
    optionsSurge: FormPickerDatas[];

    constructor(props: PricesAddTariffDispoProps) {
        super(props);
        const { t, match } = props;
        const id = match.params.id;
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PRICING_MGMT, mode: RightRank.W });
        this.state = {
            formPrice: {
                hour: null,
            },
            formData: {
                type: 'provision',
                pricing_set_id: id,
                pricing_params: {
                    time_settings: {
                        asap: {
                            is_allowed: 'on',
                        },
                        prebook: {
                            is_allowed: 'on',
                            min_delay: 0,
                        },
                    },
                    price_per_hour: [],
                    cancellation: {},
                },
                // cancellation: {max_delay: 120, ride_price_rate: 0.2}
                // extra_km_price: 3
                // max_km_per_hour: 20
            },
        };

        this.optionsSurge = generateSurgeOptions(t);
    }

    componentDidMount() {
        const { location, getTariff, getAreas } = this.props;
        const { edit_id } = queryString.parse(location.search);
        if (edit_id) {
            getTariff(edit_id);
        }
        getAreas();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.match.params.id !== (this.props.match.params as any).id) {
            const id = this.props.match.params.id;
            this.props.getTariff(id);
        }
    }

    static getDerivedStateFromProps(props: PricesAddTariffDispoProps, state: PricingPricesAddTariffDispoState) {
        const { edit_id } = queryString.parse(props.location.search);
        const { tariffs } = props;
        const { formData } = state;

        if (!formData.loaded && tariffs && tariffs[edit_id]) {
            const formatedData: any = ObjectUtils.deepCopy(tariffs[edit_id]);
            formatedData.loaded = true;

            if (formatedData?.pricing_params?.cancellation?.ride_price_rate) {
                formatedData.pricing_params.cancellation.ride_price_rate = parseFloat(
                    (formatedData.pricing_params.cancellation.ride_price_rate * 100).toFixed(2),
                );
            }

            const time_settings = formatedData?.pricing_params?.time_settings;
            if (time_settings) {
                const { asap, prebook } = time_settings;

                if (prebook?.is_allowed === 0) {
                    prebook.is_allowed = 'off';
                } else if (prebook?.is_allowed === 1) {
                    prebook.is_allowed = 'on';
                }

                if (prebook?.min_delay > 0) {
                    prebook.min_delay /= 60;
                }

                if (asap?.is_allowed === 0) {
                    asap.is_allowed = 'off';
                } else if (asap?.is_allowed === 1) {
                    asap.is_allowed = 'on';
                }
            }

            return { formData: formatedData };
        }
        return null;
    }

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    handleChange = (event) => {
        const { formPrice } = this.state;
        this.setState({
            formPrice: FormUtils.nextSubState(event, formPrice),
        });
    };

    handleDelete = (index) => (e) => {
        const { formData } = this.state;

        formData.pricing_params.price_per_hour.splice(index, 1);
        this.setState({
            formData,
        });
    };

    handleSubmitHour = (e) => {
        this.handleSubmit(e);
    };

    handleSubmit(e) {
        e.preventDefault();
        const { formData, formPrice } = this.state;

        if (formPrice.hour) {
            formData.pricing_params.price_per_hour.push(formPrice.hour);
            formPrice.hour = null;
        }
        this.setState({
            formData,
            formPrice,
        });
    }

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateInputs = (): FormDatas[] => {
        const { t, areas, match } = this.props;
        const id = match.params.id;
        const placeholder = t('tariffs.surge_options.inherit');

        const inputs: FormDatas[] = [
            {
                title: t(id ? 'tariffs.edit_dispo' : 'tariffs.new_dispo'),
                datas: [
                    {
                        id: 'start_area_id',
                        label: t('tariffs.start_area'),
                        type: 'picker',
                        searchable: true,
                        required: true,
                    },
                ],
            },
            {
                title: t('tariffs.cancellation'),
                datas: [
                    {
                        id: 'pricing_params.cancellation.max_delay',
                        label: t('tariffs.cancellation_max_delay'),
                        placeholder,
                        type: 'number',
                        inputsAttributs: { step: 1 },
                    },
                    {
                        id: 'pricing_params.cancellation.ride_price_rate',
                        label: t('tariffs.cancellation_ride_price_rate'),
                        placeholder,
                        type: 'number',
                        inputsAttributs: { step: 0.01, min: 0, max: 100 },
                    },
                ],
            },
            {
                title: t('tariffs.settings'),
                datas: [
                    {
                        id: 'pricing_params.extra_km_price',
                        label: t('tariffs.extra_km_price'),
                        placeholder,
                        type: 'number',
                    },
                    {
                        id: 'pricing_params.max_km_per_hour',
                        label: t('tariffs.max_km_per_hour'),
                        placeholder,
                        type: 'number',
                    },
                ],
            },
            {
                title: t('tariffs.time_settings.title'),
                datas: [
                    {
                        id: 'pricing_params.time_settings.asap.is_allowed',
                        label: t('tariffs.time_settings.asap'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.time_settings.prebook.is_allowed',
                        label: t('tariffs.time_settings.prebook'),
                        type: 'picker',
                        options: this.optionsSurge,
                        noEmpty: true,
                    },
                    {
                        id: 'pricing_params.time_settings.prebook.min_delay',
                        label: t('tariffs.time_settings.prebook_min_delay'),
                        type: 'number',
                        inputsAttributs: { step: 0.01, min: 0 },
                        linked: { id: 'pricing_params.time_settings.prebook.is_allowed', value: 'on' },
                    },
                ],
            },
            {
                title: t('tariffs.hour_price'),
                datas: [
                    {
                        id: 'pricing_params.hour_price',
                        label: t('tariffs.hour_price'),
                        type: 'create',
                        customView: (val, handleChange) => {
                            const { formData, formPrice } = this.state;
                            const { globalClasses } = this.props;
                            return (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 30 }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flex: 1,
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <FormInput
                                                id={'hour'}
                                                name={`hour`}
                                                type={'number'}
                                                placeholder={'1'}
                                                value={formPrice.hour}
                                                label={`${t('tariffs.hour')} (${t('common:euro_ht')})`}
                                                inputProps={{
                                                    form: 'form_dispo_price_hour',
                                                    min: 0,
                                                    // max: 99,
                                                    step: 0.01,
                                                }}
                                                required={true}
                                                disabled={false}
                                                handleChange={this.handleChange}
                                            />
                                            <TooltipButton title={t('common:add')}>
                                                <Button
                                                    aria-label="filter list"
                                                    type="submit"
                                                    variant="contained"
                                                    form="form_dispo_price_hour"
                                                    style={{
                                                        height: 56,
                                                        alignSelf: 'flex-end',
                                                        marginBottom: 10,
                                                    }}
                                                    color={
                                                        formPrice.hour !== null && String(formPrice.hour).length > 0
                                                            ? 'secondary'
                                                            : 'inherit'
                                                    }
                                                >
                                                    <Icon fontSize="small">add</Icon>
                                                </Button>
                                            </TooltipButton>
                                        </div>
                                    </div>
                                    <Listing
                                        datas={formData?.pricing_params?.price_per_hour?.map((value, index) => {
                                            return (
                                                <div
                                                    key={`hour ${index + 1} ${value}`}
                                                    style={{
                                                        flex: 'inherit',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span style={{ marginRight: 6, flex: 1 }}>
                                                        {`${t('common:hour')} ${index + 1}`}
                                                    </span>
                                                    <div style={{ marginRight: 6, flex: 1 }}>
                                                        <b>{`${value} ${t('common:euro_ht')}`}</b>
                                                    </div>
                                                    {this.isAdmin && (
                                                        <TooltipButton title={t('common:delete')}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={this.handleDelete(index)}
                                                                className={clsx(
                                                                    globalClasses.btnAutoWidth,
                                                                    globalClasses.btnDanger,
                                                                )}
                                                            >
                                                                <Icon fontSize="small">delete</Icon>
                                                            </Button>
                                                        </TooltipButton>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    />
                                </div>
                            );
                        },
                    },
                ],
            },
        ];

        if (areas) {
            inputs[0].datas.find((d) => d.id === 'start_area_id').options = Object.values(areas).map((r: Area) => {
                return { id: r.id, name: r.name };
            });
        }

        return inputs;
    };

    onSubmitTariffDispo = (formData, event) => {
        const { edit_id } = queryString.parse(this.props.location.search);

        if (!this.isAdmin) {
            return;
        }

        if (
            this.state.formData.pricing_params.price_per_hour.length !== formData.pricing_params.price_per_hour.length
        ) {
            formData.pricing_params.price_per_hour = this.state.formData.pricing_params.price_per_hour;
        }

        this.setState({
            formData: formData,
        });

        const formatedData = ObjectUtils.deepCopy(formData);
        if (!formatedData.pricing_params?.price_per_hour || formatedData.pricing_params.price_per_hour.length === 0) {
            this.alertRef.showAlert();
        } else {
            this.alertRef.closeAlert();

            delete formatedData.loaded;
            delete formatedData.history;
            if (
                formatedData?.pricing_params?.cancellation &&
                ObjectUtils.isEmpty(formatedData.pricing_params.cancellation)
            ) {
                delete formatedData.pricing_params.cancellation;
            }
            if (formatedData?.pricing_params?.cancellation?.ride_price_rate) {
                formatedData.pricing_params.cancellation.ride_price_rate /= 100;
            }

            // Format time_settings picker
            if (formatedData.pricing_params.time_settings?.prebook) {
                const prebook = formatedData.pricing_params.time_settings.prebook;
                prebook.is_allowed = PricingPricesAddTariffHoroPage.formatSurgePickerToBoolean(prebook.is_allowed);
                if (!prebook.is_allowed) {
                    delete prebook.min_delay;
                } else {
                    if (prebook.min_delay > 0) {
                        prebook.min_delay *= 60;
                    }
                }
            }
            if (formatedData.pricing_params.time_settings?.asap) {
                formatedData.pricing_params.time_settings.asap.is_allowed =
                    PricingPricesAddTariffHoroPage.formatSurgePickerToBoolean(
                        formatedData.pricing_params.time_settings.asap.is_allowed,
                    );
            }

            if (edit_id) {
                this.props.editTariff(edit_id, formatedData);
            } else {
                this.props.createTariff(formatedData);
            }
        }
    };

    render() {
        const { formData } = this.state;
        const { t, location, match, getPricesAddTariffError, tariffs, isSavingTariff } = this.props;
        const { edit_id } = queryString.parse(location.search);

        const id: string = match.params.id;
        const formPayload = edit_id ? formData : { ...formData, loaded: true };

        return (
            <App needRights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.R }}>
                <ErrorMessage payload={getPricesAddTariffError} />
                <LCAlert
                    onRef={(ref) => (this.alertRef = ref)}
                    details={t('tariffs.missing_price_dispo')}
                    type="error"
                    open={false}
                />
                {formPayload?.loaded && (
                    <>
                        <form onSubmit={this.handleSubmitHour} id="form_dispo_price_hour">
                            <input type="submit" hidden value="Ok" />
                        </form>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.pricingManagePrices}/${id}`}
                            onSubmit={this.onSubmitTariffDispo}
                            readOnly={!this.isAdmin}
                            actionBarHidden={!this.isAdmin}
                            loader={isSavingTariff}
                        />
                        <ActionLogs history={tariffs?.[edit_id]?.history} withValues allValues />
                    </>
                )}
            </App>
        );
    }
}

const PricingPricesAddTariffDispoPageWithTranslation = withGlobalStyles(
    withTranslation(['pricing', 'common'])(PricingPricesAddTariffDispoPage),
);

const mapStateToProps = (state) => ({
    areas: pricingSelectors.areas(state),
    tariffs: pricingSelectors.tariffs(state),
    getPricesAddTariffError: errorSelectors.getPricesAddTariffError(state),
    isSavingTariff: pendingSelectors.isPending(state, pricingTypes.CREATE_TARIFF, pricingTypes.EDIT_TARIFF),
});

const mapDispatchToProps = {
    getTariff: pricingActions.getTariff,
    createTariff: pricingActions.createTariff,
    editTariff: pricingActions.editTariff,
    deleteTariff: pricingActions.deleteTariff,
    getAreas: pricingActions.getAreas,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingPricesAddTariffDispoPageWithTranslation);
